// client/src/components/ticketing/OrderDetailDrawer.jsx
//
// Slide-over for a single ticket order. Shows the money breakdown + issued
// tickets, and (for completed orders with money left to refund) a refund
// form supporting full or partial amounts, with an optional count of
// tickets to cancel back into inventory.

import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

export default function OrderDetailDrawer({ tenantId, eventId, orderId, onClose, onChanged }) {
  const base = `/api/ticketing/${tenantId}/event/${eventId}/orders/${orderId}`;

  const [order, setOrder] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [mode, setMode] = useState('full'); // full | partial
  const [amount, setAmount] = useState('');
  const [cancelCount, setCancelCount] = useState(0);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(base);
      setOrder(data.order);
      setTickets(data.tickets || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  const money = (n) => `${(order?.currency || 'usd').toUpperCase()} ${Number(n || 0).toFixed(2)}`;
  const remaining = order ? Number(order.total_amount) - Number(order.refunded_amount) : 0;
  const canRefund = order && order.status === 'completed' && remaining > 0.005;

  const submitRefund = async () => {
    setSubmitting(true);
    setError('');
    setResult('');
    try {
      const body = { reason: reason || undefined };
      if (mode === 'partial') {
        const cents = Math.round(Number(amount) * 100);
        if (!cents || cents <= 0) { setError('Enter a refund amount'); setSubmitting(false); return; }
        body.amountCents = cents;
        body.cancelTicketCount = Number(cancelCount) || 0;
      }
      const { data } = await axios.post(`${base}/refund`, body);
      setResult(`Refunded ${money(data.refunded)}${data.ticketsCancelled ? ` · ${data.ticketsCancelled} ticket(s) released` : ''}`);
      await load();
      if (onChanged) setTimeout(onChanged, 900);
    } catch (err) {
      setError(err?.response?.data?.message || 'Refund failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full max-w-md h-full overflow-y-auto bg-white dark:bg-neutral-900 text-black dark:text-white shadow-xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Order #{orderId}</h3>
          <button onClick={onClose} className="text-sm opacity-60 hover:opacity-100">Close</button>
        </div>

        {loading && <div className="text-sm opacity-70">Loading…</div>}
        {error && <div className="text-sm text-red-600">{error}</div>}

        {order && (
          <>
            <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 text-sm space-y-1">
              <Row label="Buyer" value={`${order.buyer_name} · ${order.buyer_email}`} />
              <Row label="Tier" value={order.tier_name} />
              <Row label="Tickets" value={order.num_tickets} />
              <Row label="Subtotal" value={money(order.subtotal_amount)} />
              {Number(order.discount_amount) > 0 && <Row label="Discount" value={`- ${money(order.discount_amount)}`} />}
              <Row label="Total paid" value={money(order.total_amount)} />
              {Number(order.refunded_amount) > 0 && <Row label="Refunded" value={money(order.refunded_amount)} />}
              <Row label="Status" value={order.status} />
              {order.promo_code && <Row label="Promo" value={order.promo_code} />}
            </div>

            <div>
              <div className="text-xs uppercase opacity-60 mb-1">Tickets</div>
              <ul className="text-sm space-y-1">
                {tickets.map((t, i) => (
                  <li key={t.id} className="flex justify-between rounded border border-black/10 dark:border-white/10 px-2 py-1">
                    <span>{t.attendee_name || `Ticket ${i + 1}`}</span>
                    <span className="opacity-70">{t.status}</span>
                  </li>
                ))}
              </ul>
            </div>

            {canRefund ? (
              <div className="rounded-lg border border-black/10 dark:border-white/10 p-3 space-y-3">
                <div className="font-medium text-sm">Issue refund</div>
                <div className="flex gap-2 text-sm">
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={mode === 'full'} onChange={() => setMode('full')} />
                    Full ({money(remaining)})
                  </label>
                  <label className="flex items-center gap-1">
                    <input type="radio" checked={mode === 'partial'} onChange={() => setMode('partial')} />
                    Partial
                  </label>
                </div>
                {mode === 'partial' && (
                  <div className="space-y-2">
                    <input
                      type="number" min="0" step="0.01" value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={`Amount (max ${remaining.toFixed(2)})`}
                      className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
                    />
                    <label className="block text-xs opacity-70">
                      Tickets to cancel &amp; return to inventory
                      <input
                        type="number" min="0" max={order.num_tickets} value={cancelCount}
                        onChange={(e) => setCancelCount(e.target.value)}
                        className="mt-1 w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
                      />
                    </label>
                  </div>
                )}
                <input
                  value={reason} onChange={(e) => setReason(e.target.value)}
                  placeholder="Reason (optional, shown to buyer)"
                  className="w-full px-2 py-1.5 rounded border border-black/15 dark:border-white/15 bg-transparent text-sm"
                />
                <button
                  onClick={submitRefund} disabled={submitting}
                  className="w-full px-3 py-2 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50"
                >
                  {submitting ? 'Processing…' : `Refund ${mode === 'full' ? money(remaining) : (amount ? `$${amount}` : '')}`}
                </button>
              </div>
            ) : (
              <div className="text-sm opacity-60">
                {order.status === 'refunded' ? 'This order is fully refunded.' : 'No refundable balance.'}
              </div>
            )}

            {result && <div className="text-sm text-emerald-600">{result}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="opacity-60">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
